import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useGetProductMarketingItemsQuery,
} from '../../redux-modules/service';
import { Download } from '../../components/ui/icon/Download';
import { VideoIframe } from '../../components/ui/view/VideoIframe';
import { countTags } from '../../util/TagFilter';
import { TagFilterList } from '../../components/ui/view/TagFilterList';
import { ShowMoreLink } from '../../components/ui/view/ShowMoreLink';
import { Picture } from '../../components/ui/Picture';
import { ArrowUpRightFromSquare } from '../../components/ui/icon/ArrowUpRightFromSquare';
import { ShareFromSquare } from '../../components/ui/icon/ShareFromSquare';
import { Link } from '../../components/ui/icon/Link';
import { SliderCards } from '../../components/ui/view/SliderCards';

function getTypeName(typeHandle, product) {
  const typeNameMapping = {
    marketingItemBrochure: Craft.t(product, 'Brochures & Flyers'),
    marketingItemPhoto: Craft.t(product, 'Photos'),
    marketingItemVideo: Craft.t(product, 'Video'),
  };
  return typeNameMapping[typeHandle] ?? Craft.t(product, 'Other');
}

function ProductDetailMarketingItems({
  productId,
  loadingText,
}) {
  const {
    data,
    isFetching,
  } = useGetProductMarketingItemsQuery({ productId });
  const [limit, setLimit] = useState(8);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    // Initialize Fancybox after component has rendered
    if (!isFetching && data) {
      // Use a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (typeof window.initFancybox === 'function') {
          window.initFancybox();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    // Return a no-op function for the else case to satisfy ESLint
    return () => {};
  }, [isFetching, data]);

  if (isFetching || !data) {
    return (<div className="container">{loadingText}</div>);
  }

  const { total, translates } = data;
  const { product } = translates;
  let { marketingItems } = data;

  const categories = countTags(marketingItems, (item) => [getTypeName(item.type, product)]);

  if (categoryFilter) {
    marketingItems = marketingItems.filter(
      (marketingItem) => getTypeName(marketingItem.type, product) === categoryFilter,
    );
  }

  marketingItems = marketingItems.slice(0, limit);

  return (
    <div className="section-main">
      <div className="container">
        <div className="section-main-head">
          <div className="row align-item-center">
            <div className="col-12 col-lg">
              <h2>{Craft.t(product, 'Marketing items')}</h2>
            </div>
          </div>
        </div>

        <TagFilterList
          tags={categories}
          totalCount={total}
          selectedTag={categoryFilter}
          onTagSelected={(tag) => setCategoryFilter(tag)}
        />

        {marketingItems.map((item) => (
          item.files.map((marketingItem) => (
            item.type !== 'marketingItemPhoto' && (
              <div key={`popup${marketingItem.id}`} className="popup-holder">
                <div className="main-modal" id={`modal-marketing-item-${marketingItem.id}`}>
                  <div className="main-modal-close-head">
                    <h2 className="d-lg-none">{item.title}</h2>
                  </div>
                  {item.type === 'marketingItemBrochure' ? (
                    <div className="card-modal-brochure">
                      <div className="card-visual">
                        <Picture image={marketingItem.modal} defaultTransform="lg-min" loading="lazy" />
                      </div>
                      <div className="card-body">
                        <h2>{item.title}</h2>
                        {(marketingItem.file || marketingItem.pdfViewLink) && (
                          <div className="btn-wrap justify-content-between">
                            <div>
                              {marketingItem.pdfViewLink && (
                                <a
                                  className="btn btn-secondary"
                                  href={marketingItem.pdfViewLink}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                >
                                  <ArrowUpRightFromSquare />
                                  {Craft.t(product, 'View brochure')}
                                  {marketingItem.fileLanguage.length ? (
                                    <>
                                      {' - '}
                                      {marketingItem.fileLanguage.join(', ')}
                                    </>
                                  ) : ''}
                                </a>
                              )}
                              {marketingItem.file && (
                                <a className="icon-link" href={marketingItem.file.url} download="">
                                  <Download />
                                  {Craft.t(product, 'Download brochure')}
                                  {marketingItem.fileLanguage.length ? (
                                    <>
                                      {' - '}
                                      {marketingItem.fileLanguage.join(', ')}
                                    </>
                                  ) : ''}
                                </a>
                              )}
                            </div>
                            <div className="position-relative">
                              <button
                                className="btn-share icon-link dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                data-bs-auto-close="outside"
                                aria-expanded="false"
                              >
                                <ShareFromSquare />
                                <span>
                                  &nbsp;
                                  {Craft.t(product, 'Share')}
                                </span>
                              </button>
                              <div className="drop-share dropdown-menu">
                                <ul className="list-share">
                                  {marketingItem.pdfViewLink && (
                                    <li>
                                      <button type="button" data-clipboard-text={marketingItem.pdfViewLink}>
                                        <Link />
                                        <span>{Craft.t(product, 'Copy view link')}</span>
                                      </button>
                                    </li>
                                  )}
                                  {marketingItem.file && (
                                    <li>
                                      <button type="button" data-clipboard-text={marketingItem.file.url}>
                                        <Link />
                                        <span>{Craft.t(product, 'Copy download link')}</span>
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="video-wrap ratio ratio-16x9">
                        {marketingItem.modal.thumbnailUrl ? (
                          <img src={marketingItem.modal.thumbnailUrl} alt="" loading="lazy" />
                        ) : (
                          <div className="bg-wrap">
                            <Picture image={marketingItem.modal} defaultTransform="lg-min" loading="lazy" />
                          </div>
                        )}

                        {marketingItem.videoIframeSrc && (
                          <VideoIframe src={marketingItem.videoIframeSrc} />
                        )}
                      </div>
                      <h2>{item.title}</h2>
                      {(marketingItem.file || marketingItem.videoIframeSrc) && (
                        <div className="video-bar">
                          <div className="row justify-content-between align-items-center">
                            {marketingItem.file && (
                              <div className="col-12 col-lg-8 mb-2 mb-lg-0 d-flex align-items-center">
                                <a className="icon-link" href={marketingItem.file.url} target="_blank" rel="noreferrer">
                                  <Download />
                                  {Craft.t(product, 'Download file')}
                                  {marketingItem.fileLanguage.length ? (
                                    <>
                                      {' - '}
                                      {marketingItem.fileLanguage.join(', ')}
                                    </>
                                  ) : ''}
                                </a>
                                <span className="ms-4b">{`${marketingItem.file.kind} (${marketingItem.file.size})`}</span>
                              </div>
                            )}
                            <div className="col-12 col-lg text-lg-end">
                              <div className="position-relative">
                                <button
                                  className="btn-share dropdown-toggle icon-link"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  data-bs-auto-close="outside"
                                  aria-expanded="false"
                                >
                                  <ShareFromSquare />
                                  <span>
                                    &nbsp;
                                    {Craft.t(product, 'Share')}
                                  </span>
                                </button>
                                <div className="drop-share dropdown-menu">
                                  <ul className="list-share">
                                    {marketingItem.file && (
                                      <li>
                                        <button type="button" data-clipboard-text={marketingItem.file.url}>
                                          <Link />
                                          <span>{Craft.t(product, 'Copy download link')}</span>
                                        </button>
                                      </li>
                                    )}
                                    {marketingItem.videoIframeSrc && (
                                      <li>
                                        <button type="button" data-clipboard-text={marketingItem.videoIframeSrc}>
                                          <Link />
                                          <span>{Craft.t(product, 'Copy "{provider}" link', { provider: marketingItem.videoProvider })}</span>
                                        </button>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {(item.files?.length > 1) && (
                    <>
                      <span className="title-sm mb-2a mt-4">{Craft.t(product, 'This file is also available in:')}</span>
                      <ul className={item.type === 'marketingItemBrochure' ? 'list list-arrow' : 'list list-video'}>
                        {item.files.map((file) => (
                          file.fileLanguage !== marketingItem.fileLanguage && (
                            <li key={`file${file.id}`}>
                              <a href={`#modal-marketing-item-${file.id}`} className="main-modal-opener">
                                {file.fileLanguage.join(', ')}
                              </a>
                            </li>
                          )
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )
          ))
        ))}

        <SliderCards className={`slider-cards sm slick-slider${total > limit ? ' mb-2a' : ''}`}>
          {marketingItems.map((marketingItem) => (
            marketingItem.type === 'marketingItemPhoto' ? (
              marketingItem.files[0].modal.length > 0 && (
                <div key={`card${marketingItem.id}`}>
                  <a
                    className="card-bordered"
                    data-fancybox={`gallery${marketingItem.id}`}
                    href={marketingItem.files[0].modal[0].transforms.all.url || null}
                    data-caption={
                      marketingItem.files[0].modal[0].alt
                      || marketingItem.files[0].modal[0].title
                      || null
                    }
                    data-has-buttons="true"
                    data-download={marketingItem.files[0].modal[0].url || null}
                  >
                    <SliderCardInner marketingItem={marketingItem} product={product} />
                  </a>
                  {marketingItem.files[0].modal.map((item, index) => (
                    index !== 0 && (
                      <span
                        key={`gallery${marketingItem.id}-${item.id}`}
                        data-fancybox={`gallery${marketingItem.id}`}
                        data-src={item.transforms.all.url}
                        data-caption={item.alt || item.title}
                        data-has-buttons="true"
                        data-download={item.url}
                      />
                    )
                  ))}
                </div>
              )
            ) : (
              <div key={`card${marketingItem.id}`}>
                <a className={`card-bordered main-modal-opener${marketingItem.type === 'marketingItemBrochure' ? ' img-center' : ''}`} href={`#modal-marketing-item-${marketingItem.id}`}>
                  <SliderCardInner marketingItem={marketingItem} product={product} />
                </a>
              </div>
            )
          ))}
        </SliderCards>
        {limit < total && (
          <ShowMoreLink label={Craft.t(product, 'Show all marketing items')} onClick={() => setLimit(total)} linkClass="more-marketing" />
        )}
      </div>
    </div>
  );
}

function SliderCardInner(item) {
  const {
    marketingItem,
    product,
  } = item;

  return (
    <>
      <div className="card-visual">
        {marketingItem.thumbnail.thumbnailUrl ? (
          <picture>
            <img src={marketingItem.thumbnail.thumbnailUrl} alt="" loading="lazy" />
          </picture>
        ) : (
          <Picture image={marketingItem.thumbnail} defaultTransform="xl-min" loading="lazy" />
        )}
        {/* <span className="mark-video">05:23</span> */}
      </div>
      <div className="card-body">
        <h3>{marketingItem.title}</h3>
        <span className="category">{getTypeName(marketingItem.type, product)}</span>
      </div>
    </>
  );
}

ProductDetailMarketingItems.propTypes = {
  productId: PropTypes.string.isRequired,
  loadingText: PropTypes.string.isRequired,
};

export { ProductDetailMarketingItems };
